import { useRegisteredJob } from "../../hooks/useRegisteredJob";
import { useEffect, useId, useRef, useState } from "react";
import axios from "axios";
import { INSIGHT_STATUS, ISocialPromptSeriesReadWithStatus } from "../../Types/account";
import { applySeriesRevision, cancelSeriesRevision, getLatestSeriesRevision, requestSeriesRevision, PromptRevisionChange, SeriesRevision } from "../../Api/socialPromptRevisions";
import "../../Css/Common/SocialPromptRevisionPanel.css";

interface Props {
  series: ISocialPromptSeriesReadWithStatus;
  hasUnsavedChanges: boolean;
  onApplied: (changes: PromptRevisionChange[]) => void;
}

function errorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    if (error.response?.status === 409) return "This series changed while you were reviewing it. Generate a new preview.";
    if (error.response?.status === 403) return "You no longer have permission to revise this series.";
    const detail = error.response?.data?.detail;
    if (typeof detail === "string") return detail;
  }
  return "The request could not be completed. Please try again.";
}

export function SocialPromptRevisionPanel({ series, hasUnsavedChanges, onApplied }: Props) {
  const inputId = useId();
  const [open, setOpen] = useState(false);
  const [guidance, setGuidance] = useState("");
  const remaining = series.prompts.filter(p => p.interaction_status?.insight_status !== INSIGHT_STATUS.COMPLETED);
  const [selected, setSelected] = useState<number[]>(remaining.map(p => p.id));
  const selectedEditable = selected.filter(id => remaining.some(p => p.id === id));
  const [revision, setRevision] = useState<SeriesRevision | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const requestKey = useRef<{ signature: string; key: string } | null>(null);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  const live = useRegisteredJob("social_revision", open ? revision?.id : undefined, revision?.client_id ?? "");
  useEffect(() => {
    if (live.currentData) { setRevision(live.currentData); setError(""); }
    else if (live.error) {
      setError("This revision could not be refreshed. Check your access and try again.");
      if (typeof live.error === "object" && "status" in live.error && [401, 403, 404].includes(Number(live.error.status))) setRevision(null);
    }
  }, [live.currentData, live.error]);

  const generate = async () => {
    setBusy(true); setError(""); setNotice("");
    try {
      const allRemaining = selectedEditable.length === remaining.length && remaining.every(p => selectedEditable.includes(p.id));
      const payload = { guidance: guidance.trim(), scope: allRemaining ? "remaining" as const : "selected" as const, prompt_ids: allRemaining ? null : selectedEditable };
      const signature = JSON.stringify(payload);
      if (!requestKey.current || requestKey.current.signature !== signature) requestKey.current = { signature, key: crypto.randomUUID() };
      const next = await requestSeriesRevision(series.id, { ...payload, idempotency_key: requestKey.current.key });
      if (mounted.current) setRevision(next);
    } catch (e) { if (mounted.current) setError(errorMessage(e)); }
    finally { if (mounted.current) setBusy(false); }
  };

  const apply = async () => {
    if (!revision) return;
    setBusy(true); setError("");
    try {
      const next = await applySeriesRevision(series.id, revision.id);
      if (!mounted.current) return;
      setRevision(next);
      if (next.status === "applied") { onApplied(next.result?.changes ?? []); setNotice("Series updated."); setOpen(false); }
    } catch (e) {
      if (mounted.current) {
        setError(errorMessage(e));
        if (axios.isAxiosError(e) && e.response?.status === 409) setRevision(previous => previous ? { ...previous, status: "conflict" } : previous);
      }
    } finally { if (mounted.current) setBusy(false); }
  };

  const discard = async (close: boolean) => {
    setBusy(true); setError("");
    try {
      if (revision && ["queued", "ready"].includes(revision.status)) await cancelSeriesRevision(series.id, revision.id);
      if (!mounted.current) return;
      setRevision(null); requestKey.current = null;
      if (close) setOpen(false);
    } catch (e) { if (mounted.current) setError(errorMessage(e)); }
    finally { if (mounted.current) setBusy(false); }
  };

  const queued = revision?.status === "queued";
  const openPanel = async () => {
    setOpen(true); setNotice(""); setError(""); setBusy(true);
    try {
      const pending = await getLatestSeriesRevision(series.id);
      if (!mounted.current) return;
      setRevision(pending); requestKey.current = null;
      if (pending) { setGuidance(pending.request.guidance); setSelected(pending.editable_prompt_ids); }
      else setSelected(remaining.map(p => p.id));
    } catch (e) { if (mounted.current) setError(errorMessage(e)); }
    finally { if (mounted.current) setBusy(false); }
  };
  return <div className="seriesRevision">
    <button type="button" className="seriesRevision_trigger" disabled={busy || hasUnsavedChanges || !remaining.length || open} onClick={openPanel} aria-expanded={open}>
      Revise series
    </button>
    {hasUnsavedChanges && <p>Save your current edits before revising the series.</p>}
    {!remaining.length && <p>All steps are completed.</p>}
    {notice && <p role="status">{notice}</p>}
    {open && <section className="seriesRevision_panel" aria-label="Revise series">
      {!revision && <>
        <label htmlFor={inputId}>How should this series change?</label>
        <textarea id={inputId} autoFocus value={guidance} onChange={e => setGuidance(e.target.value)} maxLength={4000} rows={4} disabled={busy} placeholder="Make the messages shorter and more conversational. Lead with their recent expansion." />
        <fieldset disabled={busy}>
          <legend>Steps to revise</legend>
          {series.prompts.map(p => {
            const completed = p.interaction_status?.insight_status === INSIGHT_STATUS.COMPLETED;
            return <label key={p.id}><input type="checkbox" disabled={completed} checked={!completed && selected.includes(p.id)} onChange={e => setSelected(old => e.target.checked ? [...old, p.id] : old.filter(id => id !== p.id))} /> Step {p.step_number ?? p.number_in_series}: {p.content_type?.replaceAll("_", " ") ?? "Prompt"}{completed ? " (completed)" : ""}</label>;
          })}
        </fieldset>
        <p>You can review the changes before saving.</p>
      </>}
      {queued && <p role="status">Revising your series. You can continue reviewing the saved prompts while this runs.</p>}
      {revision?.result && <p role="status">{revision.result.summary}</p>}
      {revision?.status === "conflict" && <p>Generate a new preview using the latest saved series.</p>}
      {revision?.status === "ready" && revision.result?.changes.map(change => {
        const original = revision.snapshot.prompts.find(p => p.id === change.prompt_id);
        return <article key={change.prompt_id} className="seriesRevision_change">
          <h3>Step {original?.step_number}: {original?.content_type.replaceAll("_", " ")}</h3>
          <p>{change.explanation}</p>
          <div className="seriesRevision_compare">
            <div><h4>Current</h4>{original?.subject && <p>{original.subject}</p>}<pre>{original?.message}</pre></div>
            <div><h4>Proposed</h4>{change.subject && <p>{change.subject}</p>}<pre>{change.message}</pre></div>
          </div>
        </article>;
      })}
      {live.delayed && <p role="status">Live updates are delayed. <button type="button" onClick={live.refetch}>Refresh</button></p>}
      {error && <p role="alert">{error}</p>}
      <div className="seriesRevision_actions">
        {!revision && <button type="button" disabled={busy || !guidance.trim() || !selectedEditable.length || hasUnsavedChanges} onClick={generate}>{busy ? "Requesting preview..." : "Preview changes"}</button>}
        {revision?.status === "ready" && <button type="button" disabled={busy || hasUnsavedChanges} onClick={apply}>{busy ? "Saving..." : "Apply changes"}</button>}
        {revision && !queued && <button type="button" disabled={busy} onClick={() => discard(false)}>Revise again</button>}
        <button type="button" disabled={busy} onClick={() => discard(true)}>Cancel</button>
      </div>
    </section>}
  </div>;
}
